export interface ReportRecord {
  slug: string;
  title: string;
  h1: string;
  metaDescription: string;
  reportingPeriod: string;
  sampleSizeSummary: string;
  methodologyOverview: string;
  keyFindings: string[];
  dataPoints: {
    label: string;
    value: string;
    description: string;
  }[];
  contentSections: {
    heading: string;
    paragraphs: string[];
  }[];
  limitationsDisclaimer: string;
  relatedCitySlugs: string[];
  relatedIndustrySlugs: string[];
}

export const REPORT_REGISTRY: Record<string, ReportRecord> = {
  "peoria-local-opportunity-report": {
    slug: "peoria-local-opportunity-report",
    title: "Peoria Local Employer & Opportunity Report 2026 | BizNearYou",
    h1: "Peoria Metro Small Employer & Local Hiring Market Report",
    metaDescription: "Data report examining Peoria, IL local small business density, trade contractor momentum, healthcare practice expansion, and direct hiring accessibility.",
    reportingPeriod: "Q1-Q2 2026 Aggregate Data",
    sampleSizeSummary: "Based on 1,420+ verified independent business records in Peoria County, Tazewell County, and Woodford County.",
    methodologyOverview: "Calculated from public commercial business registrations, verified location profiles, review momentum metrics, and domain contact availability across Central Illinois.",
    keyFindings: [
      "Trade contractors and HVAC/electrical services represent the highest growth momentum sector in Tazewell and Peoria counties.",
      "78% of small businesses with 5-25 employees in Peoria do not maintain active paid job board postings.",
      "Outpatient medical and dental practices show the highest contactability rates via direct email and phone.",
      "Independent commercial service firms in Peoria Metro average 12.4 years in continuous operation."
    ],
    dataPoints: [
      { label: "Small Employers Indexed", value: "1,420+", description: "Physically operating commercial enterprises with 3-50 employees in Peoria metro." },
      { label: "Unadvertised Opportunity Rate", value: "78.4%", description: "Percentage of employers operating without active paid online job board listings." },
      { label: "Avg Contact Responsiveness", value: "68.2%", description: "Businesses with active verified phone or direct email contact channels." },
      { label: "Top Sector Density", value: "Trade Services (28%)", description: "Contracting, HVAC, electrical, and plumbing businesses form the largest commercial block." }
    ],
    contentSections: [
      {
        heading: "Executive Summary: Peoria Metropolitan Market Dynamics",
        paragraphs: [
          "Peoria's commercial landscape is defined by strong industrial roots, expanding outpatient healthcare corridors, and a thriving ecosystem of specialty trade contractors in East Peoria, Morton, Pekin, and Washington.",
          "While national job portals highlight corporate retail and chain employers, over three-quarters of local employment opportunities reside within independent companies operating quietly in regional business parks and commercial corridors."
        ]
      },
      {
        heading: "Sector-by-Sector Hiring Signal Distribution",
        paragraphs: [
          "Analysis of review volume spikes and physical equipment additions indicates that local residential contracting and commercial trade services experienced a 14% increase in operational activity during Q1 2026 compared to Q1 2025.",
          "Similarly, outpatient medical groups along Peoria's Knoxville Avenue and Route 91 corridors demonstrated steady expansion with new provider additions."
        ]
      }
    ],
    limitationsDisclaimer: "This report uses privacy-safe, aggregated, publicly available business data. Individual hiring decisions remain at the sole discretion of respective business owners.",
    relatedCitySlugs: ["peoria-il", "east-peoria-il", "pekin-il", "morton-il"],
    relatedIndustrySlugs: ["construction", "healthcare", "manufacturing"]
  },
  "illinois-local-employer-trends": {
    slug: "illinois-local-employer-trends",
    title: "Illinois State Small Business & Local Employer Trends | BizNearYou",
    h1: "Illinois Statewide Small Business Density & Opportunity Analysis",
    metaDescription: "Comprehensive report analyzing small employer distribution across Chicago Metro, Central Illinois, and regional hubs. Key hiring signals and contact trends.",
    reportingPeriod: "2026 Annual Benchmark",
    sampleSizeSummary: "Aggregated across 12,500+ verified Illinois small business profiles across 15 metropolitan areas.",
    methodologyOverview: "Data compiled from public state business entity filings, commercial web presence audits, review growth metrics, and local commute radius maps.",
    keyFindings: [
      "Regional hubs like Peoria, Springfield, and Bloomington feature 22% higher small business density per capita than dense Chicago urban cores.",
      "Professional service firms (law, accounting, real estate) maintain the highest website freshness ratings statewide.",
      "Independent automotive and repair shops display the highest rate of same-day phone contact availability."
    ],
    dataPoints: [
      { label: "Illinois Employers Indexed", value: "12,500+", description: "Verified independent commercial businesses across 15 Illinois city markets." },
      { label: "Direct Owner Reachability", value: "64.1%", description: "Percentage of businesses where owner or managing partner contact channels are accessible." },
      { label: "Suburban/Regional Focus", value: "62.5%", description: "Proportion of small employers located outside major Chicago downtown zip codes." }
    ],
    contentSections: [
      {
        heading: "Statewide Commercial Distribution Overview",
        paragraphs: [
          "Illinois hosts over 1.2 million small businesses. Beyond major corporate headquarters, independent regional employers in Central Illinois and suburban Chicago communities drive consistent employment in technical trades, healthcare, and professional services."
        ]
      }
    ],
    limitationsDisclaimer: "Aggregated statistical data excludes personal user identifying information and reflects commercial market conditions at time of publication.",
    relatedCitySlugs: ["peoria-il", "chicago-il", "springfield-il", "bloomington-il"],
    relatedIndustrySlugs: ["construction", "healthcare", "office-administration"]
  },
  "small-business-contactability-trends": {
    slug: "small-business-contactability-trends",
    title: "Small Business Contactability & Outreach Benchmark 2026 | BizNearYou",
    h1: "Small Business Contactability & Direct Response Rates Report",
    metaDescription: "Benchmark analysis evaluating direct contact channels across 10,000+ local businesses. Discover which channels yield highest response rates.",
    reportingPeriod: "Q1 2026 Benchmark Data",
    sampleSizeSummary: "Based on 10,000+ verified commercial business contact profiles.",
    methodologyOverview: "Evaluates contact availability rates across phone, official domain email, web contact forms, and physical address accessibility.",
    keyFindings: [
      "Phone inquiries yield immediate response at 82% of automotive repair shops and trade contractors.",
      "Email outreach addressed to specific named administrators achieves 3x higher response rates than generic 'info@' emails.",
      "Physical walk-in visits between 9 AM and 10 AM deliver peak contact rates at retail and trade businesses."
    ],
    dataPoints: [
      { label: "Verified Phone Channels", value: "94.2%", description: "Percentage of local business profiles with active direct landline or office phone numbers." },
      { label: "Domain Email Availability", value: "58.7%", description: "Businesses with active official domain emails or dedicated contact forms." },
      { label: "Named Manager Found", value: "42.1%", description: "Profiles where specific owner or managing partner names are publicly identified." }
    ],
    contentSections: [
      {
        heading: "Optimizing Communication Channels by Industry",
        paragraphs: [
          "Different industries prefer different outreach methods. Understanding these operational preferences helps job seekers choose the most effective communication channel."
        ]
      }
    ],
    limitationsDisclaimer: "Contactability benchmarks describe general industry averages and do not guarantee individual employer responses.",
    relatedCitySlugs: ["peoria-il", "chicago-il"],
    relatedIndustrySlugs: ["office-administration", "construction", "healthcare"]
  },
  "local-business-website-quality-trends": {
    slug: "local-business-website-quality-trends",
    title: "Local Business Digital Presence & Website Audit Report | BizNearYou",
    h1: "Local Employer Digital Presence & Website Quality Analysis",
    metaDescription: "Audit examining mobile friendliness, career page availability, and contact clarity across 8,000+ local small business websites.",
    reportingPeriod: "2026 Digital Audit",
    sampleSizeSummary: "8,200+ local commercial websites analyzed across Illinois and regional markets.",
    methodologyOverview: "Automated scan assessing HTTPS encryption, mobile layout responsiveness, career section existence, and contact form functionality.",
    keyFindings: [
      "86% of small business websites are mobile-responsive, but only 18% feature dedicated 'Careers' or 'Join Our Team' pages.",
      "Businesses without formal career pages frequently mention hiring needs on their homepage or social media accounts.",
      "Outpatient clinics and tech agencies lead in modern website design and secure HTTPS adoption."
    ],
    dataPoints: [
      { label: "Mobile Responsive", value: "86.4%", description: "Websites passing standard mobile usability guidelines." },
      { label: "Formal Career Page", value: "18.2%", description: "Websites with dedicated job or career navigation links." },
      { label: "Direct Contact Form", value: "71.5%", description: "Websites offering functional direct message forms." }
    ],
    contentSections: [
      {
        heading: "Digital Presence Gap in Small Business Hiring",
        paragraphs: [
          "The low percentage of formal career pages (18.2%) highlights why traditional job searching fails for small businesses. Lack of a 'Careers' page does NOT mean lack of hiring; rather, it reflects limited web editing resources."
        ]
      }
    ],
    limitationsDisclaimer: "Website quality scores assess technical site structure and public accessibility.",
    relatedCitySlugs: ["peoria-il", "chicago-il"],
    relatedIndustrySlugs: ["technology", "office-administration", "healthcare"]
  }
};

export function getAllReports(): ReportRecord[] {
  return Object.values(REPORT_REGISTRY);
}

export function getReportBySlug(slug: string): ReportRecord | null {
  if (!slug) return null;
  const cleanSlug = slug.trim().toLowerCase();
  return REPORT_REGISTRY[cleanSlug] || null;
}
